<template>
  <v-app-bar v-if="mobile" density="compact" elevation="2">
    <v-app-bar-nav-icon @click="drawerOpen = !drawerOpen" />
    <v-app-bar-title>profiControl</v-app-bar-title>
    <template v-slot:append>
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon variant="text" @click="toggleTheme()">
            <v-icon>{{ isDarkTheme ? "mdi-weather-night" : "mdi-weather-sunny" }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Menu.dark_mode") }}</span>
      </v-tooltip>
      <v-tooltip location="bottom">
        <template v-slot:activator="{ props }">
          <v-btn v-bind="props" icon variant="text" size="small" @click="Logout()">
            <v-icon>mdi-logout</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Menu.logout_tooltip") }}</span>
      </v-tooltip>
    </template>
  </v-app-bar>

  <v-navigation-drawer
    v-model="drawerOpen"
    :permanent="!mobile"
    elevation="2"
    :rounded="!mobile"
    :width="mobile ? 270 : 282"
    :class="{ 'nav-drawer-desktop': !mobile }"
  >
        <v-list-item v-if="!mobile" inactive @click="OnClick(Routes.start)" link>
            <v-list-item-title class="text-h6">
              <v-row>
                <v-col class="mt-2">profiControl</v-col>
                <v-col cols="auto">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-btn
                        v-bind="props"
                        icon
                        variant="text"
                        @click.stop="toggleTheme()"
                      >
                        <v-icon>
                          {{
                            isDarkTheme
                              ? "mdi-weather-night"
                              : "mdi-weather-sunny"
                          }}
                        </v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Menu.dark_mode") }}</span>
                  </v-tooltip>

                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-btn v-bind="props" icon variant="text" size="small" @click.stop="Logout()">
                        <v-icon> mdi-logout</v-icon>
                      </v-btn>
                    </template>
                    <span>{{ $t("Menu.logout_tooltip") }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-list-item-title>
        </v-list-item>

        <v-divider></v-divider>

        <v-list density="compact" nav open-strategy="single">
          <div v-for="item in MenuItems" :key="item.title">
            <v-list-group :key="item.title" v-if="HasSubItems(item)">
              <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" :prepend-icon="item.icon" :title="item.title" style="white-space: normal;"/>
              </template>

              <v-list-item
                v-for="subitem in item.subitems"
                :key="subitem.title"
                :prepend-icon="subitem.icon"
                :title="subitem.title"
                :active="route.name === subitem.routeName"
                @click="OnClick(subitem.routeName, subitem.requiresId)"
                style="white-space: normal;"
              />
            </v-list-group>

            <v-list-item
              v-if="!HasSubItems(item)"
              :prepend-icon="item.icon"
              :title="item.title"
              :active="route.name === item.routeName"
              @click="OnClick(item.routeName)"
            />
          </div>
        </v-list>

        <template v-slot:append>
          <div class="localChanger">
            <LocalChanger/>
          </div>
          <div>
            <p class="menu-footer">Version: {{ versionNumber }} | Nutzer: {{ currentLoggedInUsernameValue }}</p>
          </div>
        </template>
  </v-navigation-drawer>

  <v-dialog
      v-model="dialogLogout"
      max-width="400px"
      @click:outside="LogoutConfirm"
      @keydown.esc="LogoutConfirm"
    >
      <v-card>
        <v-card-title class="text-h5">{{ $t("Menu.logout_header") }}</v-card-title>
        <v-card-text>
          {{ $t("Menu.logout_message") }}
        </v-card-text>
        <v-card-actions class="justify-center">
          <v-btn color="blue" variant="text" @click="LogoutConfirm">{{
              $t("Menu.submit")
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useDisplay, useTheme } from "vuetify";
import { useRoute, useRouter } from "vue-router";
import type { RouteName } from "@/router";
import { Routes } from "@/router";
import LocalChanger from "./LocalChanger.vue";
import { useAuth } from "@/composables/useAuth";
import {
  hasPermissions,
  hasAnyPermission,
  getCurrentLoggedInUsername,
  Policies,
} from "@/authorization";
import { clearStorage, getNumberItemFromStorage } from "@/scripts/Common/componentStorage";
import { CUSTOMER_ID_STORAGE_KEY } from "@/components/customers/scripts";
import { ISSUER_ID_STORAGE_KEY } from "@/components/issuers/scripts";
import { useI18n } from "@/composables/useI18n";
import pkg from "../../package.json";

interface SubMenuItem {
  title: string
  icon: string
  routeName?: RouteName
  requiresId?: boolean
  authorized?: boolean
}

interface MenuItem {
  title: string
  icon: string
  routeName?: RouteName
  requiresId?: boolean
  authorized?: boolean
  subitems?: SubMenuItem[]
}

const { t } = useI18n();

const { mobile } = useDisplay();
const theme = useTheme();
const drawerOpen = ref(true);

const isDarkTheme = computed(() => theme.global.name.value === 'dark');

function toggleTheme() {
  theme.change(theme.global.name.value === 'dark' ? 'light' : 'dark');
}

const dialogLogout = ref(false);

const route = useRoute();
const router = useRouter();
const auth = useAuth();

const versionNumber = computed(() => pkg.version);

const MenuItems = computed<MenuItem[]>(() => {
  return [
    {
      title: t("Menu.operator"),
      icon: "mdi-account-tie",
      routeName: Routes.operator,
      authorized: hasPermissions(Policies.CAN_READ_OPERATOR_DATA)
    },
    {
      title: t("Menu.customers"),
      icon: "mdi-cash-register",
      subitems: [
        {
          title: t("Menu.customer_list"),
          icon: "mdi-format-list-bulleted",
          routeName: Routes.customers,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
        {
          title: t("Menu.customer_core_data"),
          icon: "mdi-account-tie",
          routeName: Routes.customerCoreData,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
        {
          title: t("Menu.customer_point_of_sales_management"),
          icon: "mdi-point-of-sale",
          routeName: Routes.customerPos,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
        {
          title: t("Menu.customer_device_management"),
          icon: "mdi-cellphone-link",
          routeName: Routes.customerDevices,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
        {
          title: t("Menu.customer_api_key_management"),
          icon: "mdi-key-variant",
          routeName: Routes.customerApiKeys,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
        {
          title: t("Menu.customer_auth_card_management"),
          icon: "mdi-card-account-details",
          routeName: Routes.customerAuthCards,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
        {
          title: t("Menu.customer_reports"),
          icon: "mdi-file",
          routeName: Routes.customerReports,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
        },
      ],
      authorized: hasPermissions(Policies.CAN_READ_CUSTOMER_DATA)
    },
    {
      title: t("Menu.issuers"),
      icon: "mdi-badge-account-horizontal",
      subitems: [
        {
          title: t("Menu.issuer_list"),
          icon: "mdi-format-list-bulleted",
          routeName: Routes.issuers,
          authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
        },
        {
          title: t("Menu.issuer_core_data"),
          icon: "mdi-account-tie",
          routeName: Routes.issuerCoreData,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
        },
        {
          title: t("Menu.issuer_cards"),
          icon: "mdi-card-plus",
          routeName: Routes.issuerCards,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
        },
        {
          title: t("Menu.issuer_webcharges"),
          icon: "mdi-cash-plus",
          routeName: Routes.issuerWebcharges,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
        },
        {
          title: t("Menu.issuer_reports"),
          icon: "mdi-file",
          routeName: Routes.issuerReports,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
        },
        {
          title: t("Menu.issuer_user_management"),
          icon: "mdi-account-multiple-outline",
          routeName: Routes.issuerUsers,
          requiresId: true,
          authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
        },
      ],
      authorized: hasPermissions(Policies.CAN_READ_ISSUER_DATA)
    },
    {
      title: t("Menu.sepa_documents"),
      icon: "mdi-cash-check",
      subitems: [
        {
          title: t("Menu.sepa_documents_operator"),
          icon: "mdi-bank-transfer",
          routeName: Routes.sepaOperator,
          authorized: hasPermissions(Policies.CAN_READ_SEPA)
        },
        {
          title: t("Menu.sepa_documents_customers"),
          icon: "mdi-bank-transfer-out",
          routeName: Routes.sepaCustomer,
          authorized: hasPermissions(Policies.CAN_READ_SEPA)
        },
        {
          title: t("Menu.sepa_documents_issuers"),
          icon: "mdi-bank-transfer-in",
          routeName: Routes.sepaIssuer,
          authorized: hasPermissions(Policies.CAN_READ_SEPA)
        },
        {
          title: t("Menu.accounting_state"),
          icon: "mdi-book-check-outline",
          routeName: Routes.accountingState,
          authorized: hasPermissions(Policies.CAN_READ_SEPA)
        },
      ].filter(m => m.authorized),
      authorized: hasPermissions(Policies.CAN_READ_SEPA)
    },
    {
      title: t("Menu.reports"),
      icon: "mdi-file-chart",
      routeName: Routes.reports,
      authorized: hasPermissions(Policies.CAN_READ_REPORTS)
    },
    {
      title: t("Menu.analytics"),
      icon: "mdi-chart-bar",
      subitems: [
        {
          title: t("Menu.transactions_report"),
          icon: "mdi-swap-horizontal",
          routeName: Routes.transactions,
          authorized: hasPermissions(Policies.CAN_READ_REPORTS)
        },
        {
          title: t("Menu.merchant_transactions_report"),
          icon: "mdi-store",
          routeName: Routes.merchantTransactions,
          authorized: hasPermissions(Policies.CAN_READ_REPORTS)
        },
        {
          title: t("Menu.issuer_transactions_report"),
          icon: "mdi-badge-account-horizontal",
          routeName: Routes.issuerTransactions,
          authorized: hasPermissions(Policies.CAN_READ_REPORTS)
        },
        {
          title: t("Menu.daily_totals_report"),
          icon: "mdi-calendar-check",
          routeName: Routes.dailyTotals,
          authorized: hasPermissions(Policies.CAN_READ_REPORTS)
        },
      ].filter(m => m.authorized),
      authorized: hasPermissions(Policies.CAN_READ_REPORTS)
    },
    {
      title: t("Menu.administration"),
      icon: "mdi-tune-variant",
      subitems: [
        {
          title: t("Menu.administration_cards"),
          icon: "mdi-card-bulleted",
          routeName: Routes.adminCards,
          authorized: hasPermissions(Policies.CAN_READ_CARD_DATA)
        },
        {
          title: t("Menu.administration_devices"),
          icon: "mdi-cellphone-link",
          routeName: Routes.adminDevices,
          authorized: hasPermissions(Policies.CAN_READ_DEVICE_DATA)
        },
        {
          title: t("Menu.administration_account_groups"),
          icon: "mdi-account-group-outline",
          routeName: Routes.adminAccountGroups,
          authorized: hasPermissions(Policies.CAN_READ_ACCOUNT_GROUP_DATA)
        },
        {
          title: t("Menu.administration_account_management"),
          icon: "mdi-account-cash",
          routeName: Routes.adminAccounts,
          authorized: hasPermissions(Policies.CAN_READ_ACCOUNT_DATA)
        },
        {
          title: t("Menu.administration_proficontrol_users"),
          icon: "mdi-account-multiple",
          routeName: Routes.adminUsers,
          authorized: hasPermissions(Policies.CAN_READ_SYSTEM_USERS)
        },
        {
          title: t("Menu.administration_password_reset"),
          icon: "mdi-lock-reset",
          routeName: Routes.adminPasswordResets,
          authorized: hasAnyPermission(Policies.CAN_RESET_ACCOUNT_USER_PASSWORDS, Policies.CAN_RESET_CUSTOMER_PASSWORDS)
        },
        {
          title: t("Menu.administration_imports"),
          icon: "mdi-database-import",
          routeName: Routes.adminImports,
          authorized: hasPermissions(Policies.CAN_IMPORT_SYSTEM_DATA)
        },
        {
          title: t("Menu.administration_scheduler_jobs"),
          icon: "mdi-clock-outline",
          routeName: Routes.schedulerJobs,
          authorized: hasPermissions(Policies.CAN_READ_SCHEDULER_JOB_STATES)
        },
      ].filter(m => m.authorized),
      authorized: hasAnyPermission(Policies.CAN_READ_CARD_DATA,
        Policies.CAN_READ_DEVICE_DATA,
        Policies.CAN_READ_ACCOUNT_GROUP_DATA,
        Policies.CAN_READ_ACCOUNT_DATA,
        Policies.CAN_READ_SYSTEM_USERS,
        Policies.CAN_IMPORT_SYSTEM_DATA)
    },
    {
      title: t("Menu.settings"),
      icon: "mdi-cog-outline",
      subitems: [
        {
          title: t("Menu.settings_general"),
          icon: "mdi-cash-lock",
          routeName: Routes.settingsGeneral,
          authorized: hasAnyPermission(Policies.CAN_READ_ISSUER_SEPA_SETTINGS, Policies.CAN_READ_CUSTOMER_SEPA_SETTINGS, Policies.CAN_READ_VAT_DETAILS)
        },
        {
          title: t("Menu.settings_issuers"),
          icon: "mdi-star",
          routeName: Routes.settingsIssuer,
          authorized: hasAnyPermission(Policies.CAN_READ_ACCOUNT_LIMIT_SETTINGS, Policies.CAN_READ_ISSUER_WEBCHARGE_SETTINGS, Policies.CAN_READ_GRATIFICATION_SETTINGS)
        },
      ].filter(m => m.authorized),
      authorized: hasAnyPermission(Policies.CAN_READ_ISSUER_SEPA_SETTINGS, Policies.CAN_READ_CUSTOMER_SEPA_SETTINGS, Policies.CAN_READ_ISSUER_WEBCHARGE_SETTINGS)
    },
    {
      title: t("Menu.user_account"),
      icon: "mdi-account",
      subitems: [
        {
          title: t("Menu.user_account_password_change"),
          icon: "mdi-clipboard-edit",
          routeName: Routes.userChangePassword,
        },
      ],
    }
  ].filter(m => m.authorized === true || m.authorized === undefined);
});

const currentLoggedInUsernameValue = computed(() => getCurrentLoggedInUsername());

const customerRoutes = new Set<RouteName>([
  Routes.customerCoreData, Routes.customerPos, Routes.customerDevices,
  Routes.customerDeviceDetail, Routes.customerApiKeys, Routes.customerAuthCards, Routes.customerReports,
]);
const issuerRoutes = new Set<RouteName>([
  Routes.issuerCoreData, Routes.issuerCards, Routes.issuerWebcharges,
  Routes.issuerReports, Routes.issuerUsers,
]);

function routeFamily(name: RouteName | string): 'customer' | 'issuer' | null {
  if (customerRoutes.has(name as RouteName)) return 'customer';
  if (issuerRoutes.has(name as RouteName)) return 'issuer';
  return null;
}

function resolveIdForFamily(family: 'customer' | 'issuer'): string {
  if (family === 'customer') {
    const sameFamily = routeFamily(route.name as string) === 'customer';
    const fromRoute = sameFamily ? Number(route.params.id) : 0;
    const id = fromRoute > 0 ? fromRoute : getNumberItemFromStorage(CUSTOMER_ID_STORAGE_KEY);
    return id > 0 ? String(id) : '0';
  }
  if (family === 'issuer') {
    const sameFamily = routeFamily(route.name as string) === 'issuer';
    const fromRoute = sameFamily ? Number(route.params.id) : 0;
    const id = fromRoute > 0 ? fromRoute : getNumberItemFromStorage(ISSUER_ID_STORAGE_KEY);
    return id > 0 ? String(id) : '0';
  }
  return '0';
}

function OnClick(routeName?: RouteName, requiresId?: boolean) {
  if (!routeName) return;
  if (mobile.value) drawerOpen.value = false;
  if (requiresId) {
    const targetFamily = routeFamily(routeName);
    const id = targetFamily ? resolveIdForFamily(targetFamily) : '0';
    router.push({ name: routeName, params: { id } });
  } else {
    router.push({ name: routeName });
  }
}

function Logout() {
  dialogLogout.value = true;
}

function LogoutConfirm() {
  dialogLogout.value = false;
  clearStorage();
  auth.logout();
  router.push({ name: Routes.login });
}

function HasSubItems(item: MenuItem): boolean {
  return (item.subitems?.length ?? 0) > 0;
}
</script>

<style scoped src="@/assets/css/scoped/menu-style.css"/>
